// Enhanced API utility with timeout, retry logic, and caching
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const isSupabaseFunctions = BACKEND_URL && BACKEND_URL.includes('supabase.co');

// API Configuration
const API_CONFIG = {
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
};

// Cache for API responses
const apiCache = new Map();

// Mapping of API endpoints to Supabase Edge Function names
const SUPABASE_FUNCTION_MAP = {
  'dashboards': 'get-dashboards',
  'dashboards/featured': 'get-featured-dashboards',
  'dashboards/categories': 'get-dashboard-categories',
  'dashboards/tools': 'get-dashboard-tools',
  'auth/me': 'auth-me',
  'auth/login': 'auth-login',
  'auth/logout': 'auth-logout',
  'auth/signup': 'auth-signup',
  'auth/refresh': 'auth-refresh',
  'upload/image': 'upload-dashboard-image',
  'upload/images': 'upload-dashboard-image',
  'blog-posts': 'blog-posts',
  'blog-posts/create': 'blog-posts',
  'blog-posts/update': 'blog-posts',
  'blog-posts/delete': 'blog-posts',
  'blog-analytics': 'blog-analytics'
};

// Cacheable endpoints (read-only operations)
const CACHEABLE_ENDPOINTS = [
  'dashboards/categories',
  'dashboards/tools',
  'dashboards/featured'
];

// Utility function to create cache key
const createCacheKey = (url, options = {}) => {
  const method = options.method || 'GET';
  const body = options.body ? JSON.stringify(options.body) : '';
  return `${method}:${url}:${body}`;
};

// Check if response is cached and still valid
const getCachedResponse = (cacheKey) => {
  const cached = apiCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < API_CONFIG.cacheTimeout) {
    return cached.data;
  }
  return null;
};

// Store response in cache
const setCachedResponse = (cacheKey, data) => {
  apiCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
};

// Clear cache for specific patterns
export const clearCache = (pattern = null) => {
  if (!pattern) {
    apiCache.clear();
    return;
  }
  
  for (const key of apiCache.keys()) {
    if (key.includes(pattern)) {
      apiCache.delete(key);
    }
  }
};

// Sleep utility for retry delays
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Enhanced fetch with timeout
const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${API_CONFIG.timeout}ms`);
    }
    throw error;
  }
};

// Retry logic wrapper
const fetchWithRetry = async (url, options = {}) => {
  let lastError;
  
  for (let attempt = 1; attempt <= API_CONFIG.retryAttempts; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options);
      
      // Don't retry on client errors (4xx) except 408, 429
      if (response.status >= 400 && response.status < 500) {
        if (response.status !== 408 && response.status !== 429) {
          return response;
        }
      }
      
      // Return successful responses
      if (response.ok || response.status < 500) {
        return response;
      }
      
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain errors
      if (error.message.includes('timeout') || 
          error.message.includes('NetworkError') ||
          error.name === 'TypeError') {
        
        if (attempt < API_CONFIG.retryAttempts) {
          console.warn(`API request failed (attempt ${attempt}/${API_CONFIG.retryAttempts}):`, error.message);
          await sleep(API_CONFIG.retryDelay * attempt); // Exponential backoff
          continue;
        }
      }
      
      throw error;
    }
  }
  
  throw lastError;
};

// Enhanced API URL generation
export const getApiUrl = (endpoint) => {
  if (isSupabaseFunctions) {
    const [basePath, queryString] = endpoint.split('?');
    const queryParams = queryString ? `?${queryString}` : '';
    
    // Handle dynamic endpoints like dashboards/:id
    if (basePath.startsWith('dashboards/') && basePath.split('/').length > 1 && !basePath.includes('featured') && !basePath.includes('categories') && !basePath.includes('tools')) {
      const dashboardId = basePath.split('/')[1];
      return `${BACKEND_URL}/get-dashboard/${dashboardId}${queryParams}`;
    }
    
    if (basePath === 'dashboards') {
      return `${BACKEND_URL}/get-dashboards${queryParams}`;
    }
    
    if (basePath.includes('delete-dashboard')) {
      return `${BACKEND_URL}/delete-dashboard${queryParams}`;
    }
    
    if (basePath.includes('update-dashboard')) {
      return `${BACKEND_URL}/update-dashboard${queryParams}`;
    }
    
    if (basePath.includes('create-dashboard') || basePath === 'dashboards' && endpoint.method === 'POST') {
      return `${BACKEND_URL}/create-dashboard${queryParams}`;
    }
    
    if (basePath.startsWith('upload/image/')) {
      return `${BACKEND_URL}/upload-dashboard-image${queryParams}`;
    }
    
    const functionName = SUPABASE_FUNCTION_MAP[basePath];
    if (functionName) {
      return `${BACKEND_URL}/${functionName}${queryParams}`;
    }
    
    console.warn(`Unmapped Supabase function endpoint: ${endpoint}`);
    return `${BACKEND_URL}/${basePath}${queryParams}`;
  }
  
  const [basePath, queryString] = endpoint.split('?');
  const queryParams = queryString ? `?${queryString}` : '';
  
  if (basePath === 'dashboards/featured') {
    return `${BACKEND_URL}/api/dashboards/featured${queryParams}`;
  }
  
  if (basePath === 'dashboards/categories') {
    return `${BACKEND_URL}/api/dashboards/categories${queryParams}`;
  }
  
  if (basePath === 'dashboards/tools') {
    return `${BACKEND_URL}/api/dashboards/tools${queryParams}`;
  }
  
  return `${BACKEND_URL}/api/${endpoint}`;
};

// Enhanced API client with caching and retry logic
export const apiClient = {
  async get(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    const cacheKey = createCacheKey(url, { method: 'GET' });
    
    // Check cache for cacheable endpoints
    const [basePath] = endpoint.split('?');
    if (CACHEABLE_ENDPOINTS.includes(basePath) && !options.skipCache) {
      const cached = getCachedResponse(cacheKey);
      if (cached) {
        console.log(`Cache hit for: ${endpoint}`);
        return cached;
      }
    }
    
    try {
      const { getSupabaseHeaders } = await import('./api');
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: {
          ...getSupabaseHeaders(),
          ...options.headers
        },
        ...options
      });
      
      if (!response.ok) {
        // For blog-posts endpoint, 400 errors are expected when no posts exist
        // Don't log these as errors to avoid console spam
        if (endpoint === 'blog-posts' && response.status === 400) {
          throw new Error(`API Error: ${response.status}`);
        }
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Cache successful responses for cacheable endpoints
      if (CACHEABLE_ENDPOINTS.includes(basePath)) {
        setCachedResponse(cacheKey, data);
      }
      
      return data;
    } catch (error) {
      // For blog-posts endpoint with 400 error, don't log as error
      if (endpoint === 'blog-posts' && error.message && error.message.includes('400')) {
        throw new Error(`Failed to fetch ${endpoint}: ${error.message}`);
      }
      console.error(`API GET request failed for ${endpoint}:`, error);
      throw new Error(`Failed to fetch ${endpoint}: ${error.message}`);
    }
  },
  
  async post(endpoint, data = null, options = {}) {
    const url = getApiUrl(endpoint);
    
    // Clear related cache entries
    if (endpoint.includes('dashboard')) {
      clearCache('dashboard');
    }
    
    try {
      const { getSupabaseHeaders } = await import('./api');
      const response = await fetchWithRetry(url, {
        method: 'POST',
        headers: {
          ...getSupabaseHeaders(),
          ...options.headers
        },
        body: data ? JSON.stringify(data) : null,
        ...options
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API POST request failed for ${endpoint}:`, error);
      throw new Error(`Failed to create/update ${endpoint}: ${error.message}`);
    }
  },
  
  async put(endpoint, data = null, options = {}) {
    const url = getApiUrl(endpoint);
    
    // Clear related cache entries
    if (endpoint.includes('dashboard')) {
      clearCache('dashboard');
    }
    
    try {
      const { getSupabaseHeaders } = await import('./api');
      const response = await fetchWithRetry(url, {
        method: 'PUT',
        headers: {
          ...getSupabaseHeaders(),
          ...options.headers
        },
        body: data ? JSON.stringify(data) : null,
        ...options
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API PUT request failed for ${endpoint}:`, error);
      throw new Error(`Failed to update ${endpoint}: ${error.message}`);
    }
  },
  
  async delete(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    
    // Clear related cache entries
    if (endpoint.includes('dashboard')) {
      clearCache('dashboard');
    }
    
    try {
      const { getSupabaseHeaders } = await import('./api');
      const response = await fetchWithRetry(url, {
        method: 'DELETE',
        headers: {
          ...getSupabaseHeaders(),
          ...options.headers
        },
        ...options
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return { success: true };
    } catch (error) {
      console.error(`API DELETE request failed for ${endpoint}:`, error);
      throw new Error(`Failed to delete ${endpoint}: ${error.message}`);
    }
  }
};

// Legacy exports for backward compatibility
export const API = `${BACKEND_URL}/api`;
export const BACKEND_URL_EXPORT = BACKEND_URL;

// Configuration utilities
export const updateApiConfig = (newConfig) => {
  Object.assign(API_CONFIG, newConfig);
};

export const getApiConfig = () => ({ ...API_CONFIG });